import {
  AttachFileOutlined,
  FileUploadOutlined,
  HouseOutlined,
  PersonOutline,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { isEmpty } from "lodash";
import isFunction from "lodash/isFunction";
import { useMemo, useRef, useState } from "react";
import { Field, useFormState } from "react-final-form";
import { v4 as uuidv4 } from "uuid";

import HtmlTextEditor from "../../inputs/HtmlTextEditor";
import SearchInputLight from "../../inputs/SearchInputLight";
import Flx from "../../layout/Flx";
import RffInputWrapper from "../shared/RffInputWrapper";
import HtmlEditor from "../../html/HtmlEditor";

const RffBasicHtmlEditor = ({
  label,
  required = false,
  suppressGrid,
  size,
  helperText,
  testing,
  name = "htmlString",
  disabled,
  placeholder = "Start typing to add new comment... (Or paste images directly with Ctrl+V)",
  sx,

  onChange,
}) => {
  // Subscribe so we can see true initialValues (even if async)
  const { values, initialValues } = useFormState({
    subscription: { values: true, initialValues: true },
  });

  const styles = useMemo(() => {
    return {
      border: "1px solid #dde0e4",
      borderRadius: "6px",
      overflow: "hidden",
      position: "relative",
      ...sx,
    };
  }, [sx]);
  const formKey = useRef(uuidv4());
  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      testing={testing}
      required={required}
      helperText={helperText}
    >
      <Flx column fw gap={0} sx={styles}>
        <Field
          name={name}
          initialValue={initialValues?.htmlString || ""}
          subscription={{
            value: true,
            initial: true,
            error: true,
            touched: true,
          }}
          render={({ input, meta }) => {
            // Only use the initial value, don't pass input.value which changes on every keystroke
            const initialHtml = meta.initial || initialValues?.htmlString || "";

            return (
              <HtmlEditor
                // Use a stable key based on initial value to force remount only when form resets
                key={`editor-${meta.initial || "empty"}`}
                minHeight={60}
                value={initialHtml} // Pass initial value only
                suppressOuterBorder
                disabled={disabled}
                placeholder={placeholder}
                onChange={(v) => {
                  // Update Final Form without triggering re-render of HtmlTextEditor
                  const newHtml =
                    typeof v === "string" ? v : v?.htmlString || "";
                  input.onChange(newHtml);
                  if (isFunction(onChange)) onChange(v);
                }}
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
              />
              // <HtmlTextEditor
              //   // Use a stable key based on initial value to force remount only when form resets
              //   key={`editor-${meta.initial || "empty"}`}
              //   value={initialHtml} // Pass initial value only
              //   suppressOuterBorder
              //   disabled={disabled}
              //   placeholder={placeholder}
              //   onChange={(v) => {
              //     // Update Final Form without triggering re-render of HtmlTextEditor
              //     const newHtml =
              //       typeof v === "string" ? v : v?.htmlString || "";
              //     input.onChange(newHtml);
              //     if (isFunction(onChange)) onChange(v);
              //   }}
              //   error={meta.touched && !!meta.error}
              //   helperText={meta.touched && meta.error}
              // />
            );
          }}
        />
      </Flx>
    </RffInputWrapper>
  );
};

/**
 * Attachment utility row (unchanged)
 */
const AttachmentUtilsRow = ({
  loan,
  attachmentIds,
  onAdd,
  onFileUploadClick,
}) => {
  return (
    <Flx gap={1}>
      <AttachmentUtilButton
        tooltip="Upload New File (Images, PDFs, Documents)"
        icon={<FileUploadOutlined className="thin" />}
        onClick={onFileUploadClick}
      />

      {isEmpty(loan?.loanDocuments) ? null : (
        <AttachmentUtilDropdownButton
          tooltip="Select Existing File"
          icon={<AttachFileOutlined className="thin" />}
          enableSearch
          options={loan?.loanDocuments?.map((b) => ({
            _id: b._id,
            label: b?.file_display_name,
            icon: <AttachFileOutlined className="thin" />,
          }))}
          onClick={(id) => onAdd(id, "loanDocuments")}
          attachmentIds={attachmentIds}
          attachmentKey="loanDocuments"
        />
      )}

      <AttachmentUtilDropdownButton
        tooltip="Tag/Assign Borrower"
        icon={<PersonOutline className="thin" />}
        options={loan?.borrowers?.map((b) => ({
          _id: b._id,
          label: `${b.firstName || ""} ${b.lastName || ""}`,
          icon: <PersonOutline className="thin" />,
        }))}
        onClick={(id) => onAdd(id, "borrowers")}
        attachmentIds={attachmentIds}
        attachmentKey="borrowers"
      />

      <AttachmentUtilDropdownButton
        tooltip="Tag/Assign Property"
        icon={<HouseOutlined className="thin" />}
        options={loan?.subjectProperties?.map((p) => ({
          _id: p._id,
          label: p?.address?.fullAddress,
          icon: <HouseOutlined className="thin" />,
        }))}
        onClick={(id) => onAdd(id, "subjectProperties")}
        attachmentIds={attachmentIds}
        attachmentKey="subjectProperties"
      />
    </Flx>
  );
};

const AttachmentUtilDropdownButton = ({
  tooltip,
  icon,
  onClick,
  disabled,
  options,
  enableSearch,
  attachmentIds,
  attachmentKey,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!options || options.length === 0) {
      if (isFunction(onClick)) onClick();
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchValue("");
  };

  const handleMenuItemClick = (option) => {
    if (isFunction(onClick)) onClick(option._id);
    setAnchorEl(null);
    setSearchValue("");
  };

  const filteredOptions = useMemo(() => {
    if (!options) return [];

    let filtered = options;

    if (attachmentIds && attachmentKey && attachmentIds[attachmentKey]) {
      filtered = filtered.filter(
        (option) => !attachmentIds[attachmentKey].includes(option._id),
      );
    }

    if (enableSearch && searchValue && searchValue.trim()) {
      const term = searchValue.toLowerCase().trim();
      filtered = filtered.filter((option) =>
        option.label.toLowerCase().includes(term),
      );
    }

    return filtered;
  }, [options, searchValue, enableSearch, attachmentIds, attachmentKey]);

  return (
    <>
      <AttachmentUtilButton
        tooltip={tooltip}
        icon={icon}
        onClick={handleClick}
        disabled={disabled}
      />

      {options && options.length > 0 && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          sx={{
            "& .MuiPopover-paper": {
              mt: 1,
              border: "1px solid #dde0e4",
              borderRadius: "4px",
              minWidth: "200px",
              maxWidth: "400px",
            },
          }}
        >
          <Box sx={{ width: "100%" }}>
            {enableSearch && (
              <Box sx={{ px: 1.4, py: 1, borderBottom: "1px solid #dde0e4" }}>
                <SearchInputLight
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder="Search..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      setSearchValue("");
                    }
                  }}
                />
              </Box>
            )}

            <List
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                py: 0.5,
              }}
              disablePadding
            >
              {filteredOptions.length === 0 ? (
                <ListItem
                  sx={{
                    pl: 1.4,
                    fontStyle: "italic",
                    color: grey[600],
                    cursor: "default",
                  }}
                >
                  <ListItemText
                    primary="No options available"
                    primaryTypographyProps={{ fontSize: "12px" }}
                  />
                </ListItem>
              ) : (
                filteredOptions.map((option, index) => (
                  <ListItem
                    key={option._id || index}
                    onClick={() => handleMenuItemClick(option)}
                    sx={{
                      pl: 1.4,
                      pr: 1.4,
                      py: 0.5,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: grey[100] },
                      ".MuiListItemIcon-root": {
                        minWidth: "26px",
                        ".MuiSvgIcon-root": { fontSize: "16px" },
                      },
                    }}
                  >
                    {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{ fontSize: "11px" }}
                    />
                    {option.endIcon && (
                      <ListItemIcon>{option.endIcon}</ListItemIcon>
                    )}
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Popover>
      )}
    </>
  );
};

const AttachmentUtilButton = ({ tooltip, icon, onClick, disabled }) => {
  return (
    <Tooltip title={tooltip}>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        size="small"
        variant="text"
        sx={{
          borderRadius: "8px",
          border: "1px solid #dde0e4",
          ".MuiButton-icon": { mr: 0.2 },
          ".MuiSvgIcon-root": { fontSize: "14px !important" },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};

export default RffBasicHtmlEditor;
